<script lang="ts">
	import {
		groupMainFilter,
		roomsGroupsFilter,
		resetGroupMainFilter,
		getSelectedGroupFilterCount,
	} from 'src/stores/ui/filters';
	import { ComponentType } from '@Shelly/shelly-design-system/helpers/enums';
	import { format } from '../../../services/i18n';

	import {
		Toolbar,
		Breadcrumbs,
		IconButton,
		Input,
		Chip,
		Button,
		BreadcrumbsFlexWrapper,
	} from '@Shelly/shelly-design-system';
	import RoomToolbar from '../Rooms/RoomToolbar.svelte';
	import RoomBreadCrumbs from '../Rooms/RoomBreadCrumbs.svelte';

	import { capFirstChar } from '../../../helpers/utilities';
	import { isSubAccount } from 'src/helpers/user_helpers';
	import GroupsFilterModal from './GroupsFilterModal.svelte';
	import { is_mobile } from 'src/stores/ui/viewport';
	import { edit } from 'src/stores/ui/edit_lists';
	import { defaultSelectedRoom } from 'src/helpers/constants';

	export let type = 'groups';
	export let id;

	let openFilterModal = false;
	export let disableReorder: boolean = false;

	$: notification =
		getSelectedGroupFilterCount($roomsGroupsFilter) > 0
			? ({
					title: getSelectedGroupFilterCount($roomsGroupsFilter),
					type: ComponentType.DANGER,
				} as const)
			: null;

	function resetData() {
		resetGroupMainFilter();
		roomsGroupsFilter.set({ rooms: [defaultSelectedRoom] });
	}
</script>

<Toolbar
	class="mb-m"
	isSticky
	useNewLayout
	hideSlotLeftSide={$edit || (!$is_mobile && type !== 'room')}
	hideSlotRightSide={($is_mobile && type !== 'room') || !notification}
	hideSlotBottom={!notification || $edit || ($is_mobile && type !== 'room')}
>
	<svelte:fragment slot="breadcrumbs">
		{#if type === 'room'}
			<RoomBreadCrumbs {id} />
		{:else if $is_mobile && type !== 'room'}
			<BreadcrumbsFlexWrapper hideSlotRight={!notification} gap="s">
				<div slot="left" class="flex-1 min-width-0 max-width-fit-content">
					<Breadcrumbs
						listClasses="width-full"
						aria-label={$format('label_groups', { default: 'Groups' })}
						links={[{ label: capFirstChar($format('tab_groups')) }]}
					/>
				</div>

				<Button
					slot="right"
					size="m"
					variant="secondary"
					class="align-self-center"
					icon={$edit ? '' : 'fa-solid fa-arrows-up-down-left-right'}
					disabled={disableReorder}
					on:click={() => {
						edit.set(!$edit);
					}}
				>
					{$edit
						? $format('label_finish_reordering', { default: 'Finish reordering' })
						: $format('label_reorder_items', { default: 'Reorder items' })}
				</Button>
			</BreadcrumbsFlexWrapper>
		{:else if !$is_mobile && type !== 'room'}
			<BreadcrumbsFlexWrapper gap="s" hideSlotRight={!notification}>
				<div
					class="d-flex align-items-center flex-1 min-width-0 max-width-fit-content"
					slot="left"
				>
					<Breadcrumbs
						class="flex-1 min-width-0 mr-s"
						listClasses="width-full"
						links={[{ label: capFirstChar($format('tab_groups')) }]}
						aria-label={$format('label_groups', { default: 'Groups' })}
					/>

					{#if !$edit}
						<div class="d-flex align-items-center justify-content-center">
							<Input
								class="width-md-l mr-sm-s text-capitalize-placeholder"
								bind:value={$groupMainFilter}
								type="text"
								label={capFirstChar($format('search', { default: 'Search' }))}
								icon="fas fa-search"
								id="groups-toolbar-search-input"
								aria-labelledby="groups-toolbar-search-input"
							/>
							<IconButton
								icon="fa-solid fa-filter"
								on:click={() => {
									openFilterModal = true;
								}}
								aria-label={$format('aria_open_filter', { default: 'Open filter' })}
								{notification}
							/>
						</div>
					{/if}
				</div>

				<Button
					slot="right"
					size="m"
					variant="secondary"
					class="align-self-center"
					icon={$edit ? '' : 'fa-solid fa-arrows-up-down-left-right'}
					disabled={disableReorder}
					on:click={() => {
						edit.set(!$edit);
					}}
				>
					{$edit
						? $format('label_finish_reordering', { default: 'Finish reordering' })
						: $format('label_reorder_items', { default: 'Reorder items' })}
				</Button>
			</BreadcrumbsFlexWrapper>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="leftSide">
		{#if type === 'room'}
			<RoomToolbar />
		{/if}

		{#if type === 'groups'}
			<div class="d-flex align-items-center justify-content-center">
				<Input
					class="width-md-l mr-sm-s text-capitalize-placeholder"
					bind:value={$groupMainFilter}
					type="text"
					label={capFirstChar($format('search', { default: 'Search' }))}
					icon="fas fa-search"
					id="groups-toolbar-search-input"
					aria-labelledby="groups-toolbar-search-input"
				/>
				<IconButton
					icon="fa-solid fa-filter"
					on:click={() => {
						openFilterModal = true;
					}}
					aria-label={$format('aria_open_filter', { default: 'Open filter' })}
					{notification}
				/>
			</div>
		{/if}
		{#if notification && $is_mobile}
			<div class="d-flex align-items-center">
				<IconButton
					on:click={resetData}
					icon="fas fa-xmark"
					variant="secondary"
					class="width-full"
				>
					{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
				</IconButton>
			</div>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="bottom">
		{#if notification}
			<div class="d-flex align-items-center">
				{#if !$is_mobile}
					{#each $roomsGroupsFilter.rooms as room}
						<Chip
							aria-label={room.title}
							showChip
							title={room.title}
							class="mr-s flex-shrink-0"
							on:close={resetData}
						/>
					{/each}
				{/if}
				<IconButton
					on:click={resetData}
					icon="fas fa-xmark"
					variant="secondary"
					class="width-full"
				>
					{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
				</IconButton>
			</div>
		{/if}
	</svelte:fragment>
</Toolbar>

<!-- Filter Modal -->
{#if openFilterModal}
	<GroupsFilterModal bind:openFilterModal />
{/if}
